import React, { useCallback } from 'react'
import { useFindElementFromCanvasRelativePositionInScreenSpace } from '../useFindElementFromCanvasRelativePositionInScreenSpace'
import { findPointRelativeToCanvasInScreenSpaceFromMouseEvent } from './findPointRelativeToCanvasInScreenSpaceFromMouseEvent'
import { SketchElement } from '../types'

/**
 * Hook creating the handler for the click event on the canvas.
 */
export function useCanvasClickHandler(
  sketchSceneRootElement: SketchElement | null,
  onClick: (element: SketchElement | null) => void,
  doesSketchElementAllowUserInteraction?: (
    element: SketchElement,
    event: React.MouseEvent<HTMLElement>
  ) => boolean
) {
  const findTargetElementFromCanvasRelativePositionInScreen = useFindElementFromCanvasRelativePositionInScreenSpace(
    sketchSceneRootElement
  )

  const clickHandler = useCallback(
    (reactEvent: React.MouseEvent<HTMLElement>) => {
      const pointRelativeToCanvasInScreenSpace = findPointRelativeToCanvasInScreenSpaceFromMouseEvent(
        reactEvent
      )

      if (!pointRelativeToCanvasInScreenSpace) {
        return null
      }

      const matchingElement = findTargetElementFromCanvasRelativePositionInScreen(
        pointRelativeToCanvasInScreenSpace,
        element =>
          doesSketchElementAllowUserInteraction?.(element, reactEvent) ?? true
      )
      onClick(matchingElement)
    },
    [
      doesSketchElementAllowUserInteraction,
      findTargetElementFromCanvasRelativePositionInScreen,
      onClick,
    ]
  )

  return clickHandler
}
